import type { KeyboardEvent } from "react";
import { isLeftBower, isRightBower } from "../engine/cards";
import type { Card, Suit } from "../engine/types";
import type { Locale } from "../i18n";
import { suitText, t } from "../i18n";

const SUIT_CODE = { clubs: "C", diamonds: "D", hearts: "H", spades: "S" } as const;
const RANK_CODE = { "9": "9", "10": "10", J: "j", Q: "q", K: "k", A: "a" } as const;

interface CardViewProps {
  card?: Card;
  hidden?: boolean;
  trump?: Suit | null;
  locale: Locale;
  playable?: boolean;
  selected?: boolean;
  compact?: boolean;
  onClick?: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void;
  tabIndex?: number;
}

export function CardView({
  card,
  hidden,
  trump,
  locale,
  playable = false,
  selected = false,
  compact = false,
  onClick,
  onKeyDown,
  tabIndex,
}: CardViewProps) {
  if (hidden || !card) {
    return (
      <div className={`playing-card card-back ${compact ? "compact" : ""}`} aria-hidden="true">
        <img src="/assets/imagegen/card-back.png" alt="" />
      </div>
    );
  }
  const name = `${SUIT_CODE[card.suit]}${RANK_CODE[card.rank]}`;
  const left = Boolean(trump && isLeftBower(card, trump));
  const right = Boolean(trump && isRightBower(card, trump));
  const label = `${card.rank} ${suitText[locale][card.suit]}${left ? `, ${t(locale, "leftBadge")}` : right ? `, ${t(locale, "rightBadge")}` : ""}`;
  const content = (
    <>
      <img src={`/assets/cards/${name}.svg`} alt="" aria-hidden="true" />
      {(left || right) && <span className="bower-badge">{t(locale, left ? "leftBadge" : "rightBadge")}</span>}
    </>
  );
  if (!onClick) {
    return <div className={`playing-card ${compact ? "compact" : ""}`} role="img" aria-label={label}>{content}</div>;
  }
  return (
    <button
      type="button"
      className={`playing-card card-button ${playable ? "playable" : "illegal"} ${selected ? "selected" : ""} ${compact ? "compact" : ""}`}
      aria-label={label}
      aria-pressed={selected}
      onClick={onClick}
      onKeyDown={onKeyDown}
      tabIndex={tabIndex}
      data-card-id={card.id}
    >
      {content}
    </button>
  );
}
